/**
 * Drawdown Management View
 *
 * Comprehensive capital call management interface with:
 * - Drawdown listing and filtering
 * - Status management and workflow
 * - Payment tracking and reconciliation
 * - PDF generation and download
 * - Integration with general ledger
 */

import { useState, useEffect, useEffectEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '../ui/button';
import { Tabs, TabsContent } from '../ui/tabs';
import { Alert } from '../ui/alert';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { Plus, RefreshCw, AlertCircle, Grid3x3 } from 'lucide-react';

import { useDrawdownDashboard } from '../../hooks/react-query/useDrawdowns';
import { PrepaymentPoolDashboard } from './PrepaymentPoolDashboard';
import { DrawdownsListTab } from './DrawdownsListTab';
import { OverduePaymentsTab } from './OverduePaymentsTab';
import { DrawdownRecalculationModal } from './DrawdownRecalculationModal';
import type {
  DrawdownFilters,
  DrawdownGroup,
  DrawdownSummary,
} from '../../types/drawdown';
import type { Fund, FundSummary } from '../../types/fund';

// Each sub-section titles the page, so its content doesn't repeat the heading
const SECTION_HEADINGS: Record<string, { title: string; description: string }> =
  {
    list: {
      title: 'Drawdown Management',
      description: 'Capital calls, batches and payment tracking',
    },
    overdue: {
      title: 'Overdue Payments',
      description: 'Investors with outstanding payments past due date',
    },
    prepayment: {
      title: 'Prepayment Pool',
      description: 'Balances held from regulatory capital calls',
    },
  };

// Aggregate payment progress for a drawdown, derived from how many of its
// investors have fully paid. Matches the DrawdownFilters.payment_status values.
function derivePaymentStatus(
  drawdown: DrawdownSummary
): 'unpaid' | 'partial' | 'paid' {
  const paid = drawdown.investors_paid_count;
  const total = drawdown.investors_total_count;
  if (total > 0 && paid >= total) return 'paid';
  if (paid > 0 || drawdown.total_amount_received > 0) return 'partial';
  return 'unpaid';
}

function transformToDrawdownGroup(
  drawdown: DrawdownSummary,
  fundName: string
): DrawdownGroup {
  return {
    drawdownId: drawdown.id,
    drawdownNumber: `DD-${drawdown.drawdown_number}`,
    fundName,
    purpose: drawdown.purpose,
    description: drawdown.description,
    totalAmount: drawdown.total_amount,
    status: drawdown.status,
    issueDate: drawdown.issue_date,
    batches: [], // Batches loaded on-demand in DrawdownsListTab
    createdAt: drawdown.created_at,
    lastUpdated: drawdown.created_at,
    hasRecalculationNeeded: false,
    totalInvestorCount: drawdown.investors_total_count || 0,
    totalBatchCount: drawdown.batch_count || 0,
    raisedByParentFund: drawdown.raised_by_parent_fund,
  };
}

interface DrawdownManagementViewProps {
  fundId?: string;
  fund?: Fund;
  funds?: FundSummary[]; // Add funds array for non-fund-scoped contexts
  className?: string;
  onInvestorCountAvailable?: (count: number) => void;
}

export function DrawdownManagementView({
  fundId,
  fund,
  funds,
  className,
  onInvestorCountAvailable,
}: DrawdownManagementViewProps) {
  const navigate = useNavigate();
  // Sub-tab state management with URL persistence (using 'subsection' parameter)
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get('subsection') || 'list';

  const handleSectionChange = (newSection: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('subsection', newSection);
      return newParams;
    });
  };

  // State management
  const [filters, setFilters] = useState<DrawdownFilters>({});
  const [recalculateDrawdownId, setRecalculateDrawdownId] = useState<
    string | null
  >(null);

  // React Query hooks
  const { drawdowns, totalCount, isLoading, isError, error, refetchAll } =
    useDrawdownDashboard(fundId, filters);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Keyboard shortcuts
  useKeyboardShortcuts('drawdown-management', [
    ...(fundId
      ? [
          {
            key: 'n',
            description: 'New Drawdown',
            action: () => navigate(`/app/funds/${fundId}/drawdowns/new`),
            category: 'Actions',
          },
        ]
      : []),
    {
      key: 'ArrowRight',
      description: 'Next Tab',
      action: () => {
        const sections = ['list', 'overdue'];
        if (fundId) sections.push('prepayment');
        const currentIndex = sections.indexOf(activeSection);
        const nextIndex = (currentIndex + 1) % sections.length;
        handleSectionChange(sections[nextIndex]);
      },
      category: 'Navigation',
    },
    {
      key: 'ArrowLeft',
      description: 'Previous Tab',
      action: () => {
        const sections = ['list', 'overdue'];
        if (fundId) sections.push('prepayment');
        const currentIndex = sections.indexOf(activeSection);
        const prevIndex =
          currentIndex === 0 ? sections.length - 1 : currentIndex - 1;
        handleSectionChange(sections[prevIndex]);
      },
      category: 'Navigation',
    },
  ]);

  const notifyInvestorCount = useEffectEvent((count: number) => {
    if (onInvestorCountAvailable) {
      onInvestorCountAvailable(count);
    }
  });

  const investorCount = drawdowns[0]?.investors_total_count || 0;

  useEffect(() => {
    if (investorCount > 0) {
      notifyInvestorCount(investorCount);
    }
  }, [investorCount]);

  const handleFilterChange = (
    key: keyof DrawdownFilters,
    value: string | undefined
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Batch-related handlers
  const handleRecalculate = (drawdownId: string) => {
    setRecalculateDrawdownId(drawdownId);
  };

  const handlePreviewDrawdown = (drawdownId: string) => {
    if (!fundId) return;
    navigate(`/app/funds/${fundId}/drawdowns/${drawdownId}/preview`);
  };

  const handlePreviewBatch = (drawdownId: string, batchId: string) => {
    if (!fundId) return;
    navigate(
      `/app/funds/${fundId}/drawdowns/${drawdownId}/preview?batchId=${batchId}`
    );
  };

  // Client-side filtering
  const fundName = fund?.display_name || fund?.name || 'Unknown Fund';

  // Prepayment has no panel without a fund, so the list is what's on screen
  const headingSection =
    activeSection === 'prepayment' && !fundId ? 'list' : activeSection;
  const heading = SECTION_HEADINGS[headingSection] ?? SECTION_HEADINGS.list;

  const drawdownGroups: DrawdownGroup[] = drawdowns
    .filter((drawdown) => {
      // Search filter: match against drawdown number or purpose
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesNumber = `DD-${drawdown.drawdown_number}`
          .toLowerCase()
          .includes(searchLower);
        const matchesPurpose = drawdown.purpose
          .toLowerCase()
          .includes(searchLower);
        if (!matchesNumber && !matchesPurpose) return false;
      }

      // Date range filter: match against issue date
      if (filters.date_from && drawdown.issue_date < filters.date_from)
        return false;
      if (filters.date_to && drawdown.issue_date > filters.date_to)
        return false;

      // Payment status filter: derived from paid-vs-total investor counts
      if (
        filters.payment_status &&
        derivePaymentStatus(drawdown) !== filters.payment_status
      )
        return false;

      return true;
    })
    .map((drawdown) => transformToDrawdownGroup(drawdown, fundName));

  return (
    <div className={`${className}`}>
      {/* Header: the active section titles the page, actions sit beside it */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <div>
          <h2 className="text-lg font-semibold">{heading.title}</h2>
          <p className="text-sm text-muted-foreground">{heading.description}</p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchAll()}
            disabled={isLoading}
            className="h-9"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>

          {fundId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/app/funds/${fundId}/drawdowns/matrix`)}
              className="h-9 flex items-center gap-2"
            >
              <Grid3x3 className="h-4 w-4" />
              Payment matrix
            </Button>
          )}

          {fundId && (
            <Button
              onClick={() => navigate(`/app/funds/${fundId}/drawdowns/new`)}
              variant="default"
              size="sm"
              className="h-9 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Drawdown
            </Button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {isError && error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          {error instanceof Error
            ? error.message
            : 'Failed to load drawdown data'}
        </Alert>
      )}

      {/* Tab Content */}
      <Tabs
        value={activeSection}
        onValueChange={handleSectionChange}
        className="space-y-4"
      >
        {/* Drawdowns List */}
        <TabsContent value="list">
          <DrawdownsListTab
            drawdownGroups={drawdownGroups}
            totalCount={totalCount}
            isLoading={isLoading}
            fundId={fundId}
            funds={funds}
            currentPage={currentPage}
            pageSize={pageSize}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={() => setFilters({})}
            onCreateNew={
              fundId
                ? () => navigate(`/app/funds/${fundId}/drawdowns/new`)
                : undefined
            }
            onRecalculate={handleRecalculate}
            onPreviewDrawdown={handlePreviewDrawdown}
            onPreviewBatch={handlePreviewBatch}
            onPageChange={setCurrentPage}
          />
        </TabsContent>

        {/* Overdue Items */}
        <TabsContent value="overdue">
          <OverduePaymentsTab fundId={fundId} />
        </TabsContent>

        {/* Prepayment Pool */}
        {fundId && (
          <TabsContent value="prepayment">
            <PrepaymentPoolDashboard fundId={fundId} />
          </TabsContent>
        )}
      </Tabs>

      {/* Recalculation Modal */}
      {recalculateDrawdownId && fundId && (
        <DrawdownRecalculationModal
          open={!!recalculateDrawdownId}
          onClose={() => setRecalculateDrawdownId(null)}
          fundId={fundId}
          drawdownId={recalculateDrawdownId}
          onSuccess={() => {
            setRecalculateDrawdownId(null);
            refetchAll();
          }}
        />
      )}
    </div>
  );
}
